import { Dictionary, Location } from "vue-router/types/router";
import { checkForServiceWorkerUpdate, reloadIfUpdateIsAvailable } from "../service-worker-window";
import { checkUser, logNavigation } from "./guards";

import { AccountRoutes } from "./routes/account";
import { DashboardRoutes } from "./routes/dashboard";
import { ENVIRONMENT } from "../util/environment";
import { EditorRoutes } from "./routes/editor";
import { MapRoutes } from "./routes/map";
import { ProjectsRoutes } from "./routes/projects";
import { PublicRoutes } from "./routes/public";
import ROUTES from "./routes";
import { ShareRoutes } from "./routes/share";
import { SitesRoutes } from "./routes/sites";
import VueRouter from "vue-router";
import { WorkspacesRoutes } from "./routes/workspaces";
import { loadSelectedWorkspace } from "../stores/site/effects/initialiseSelectedWorkspace";
import { sharedSiteRedirect } from "./shared-site-redirect";
import { userStore } from "../stores/user/store";

const router = new VueRouter({
  base: ENVIRONMENT.BASE_URL,
  mode: "history",
  routes: [
    ...DashboardRoutes,
    ...SitesRoutes,
    ...AccountRoutes,
    ...PublicRoutes,
    ...MapRoutes,
    ...ShareRoutes,
    ...WorkspacesRoutes,
    ...EditorRoutes,
    ...ProjectsRoutes,
    {
      path: "*",
      redirect: "/map",
    },
  ],
});

export enum REDIRECT_OPTIONS {
  SHARED_SITE = "sharedSite",
}

router.beforeEach(async (to, from, next) => {
  const { user } = userStore;
  // Redirect legacy public map URL to new share URL
  if (to.name === ROUTES.PUBLIC_MAP) {
    if (to.query.siteId) {
      return next({ path: `/${ROUTES.SHARE}/${to.query.siteId}` });
    } else {
      return next({ name: ROUTES.MAP });
    }
  }
  const userCheck = await checkUser({ user, routeName: to.name });
  let redirectURL: Location | undefined;

  checkForServiceWorkerUpdate();

  if (!userCheck.authenticated) {
    next({
      name: ROUTES.LOGOUT,
      params: { error: userCheck.error || "" },
    });
    return;
  }

  if (userCheck?.attemptRedirect === REDIRECT_OPTIONS.SHARED_SITE) {
    redirectURL = await sharedSiteRedirect({ routeName: to.name || "" });
  }
  if (redirectURL) {
    logNavigation(to.name || "", redirectURL.name);
    return next(redirectURL);
  }

  loadSelectedWorkspace();

  next();
});

router.afterEach((to, from) => {
  logNavigation(to.name || "");
  if (from.fullPath !== "/" && to.fullPath.split("/")[1] !== from.fullPath.split("/")[1]) {
    reloadIfUpdateIsAvailable();
  }
});

type WithoutRefreshParams = {
  name?: string;
  params?: Dictionary<string>;
  query?: Dictionary<string>;
};

const redirectWithoutMapRefresh = ({ name, params, query }: WithoutRefreshParams) => {
  const { href } = router.resolve({
    name,
    params,
    query,
  });
  window.history.pushState({}, "", href);
};

export { router, redirectWithoutMapRefresh };
